import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Box,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Collapse,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Divider,
  useColorModeValue,
  Icon,
  Flex,
  Spinner,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FiUploadCloud, FiFile, FiCheckCircle, FiXCircle, FiSettings } from 'react-icons/fi';
import { useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';

const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/csv': ['.csv'],
  'application/json': ['.json'],
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const VectorStoreCreationModal = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  institution = '',
  agentTypes = []
}) => {
  const [mode, setMode] = useState('create'); // 'create' or 'existing'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    textContent: '',
    chunkSize: 1000,
    chunkOverlap: 200,
    institution: institution,
    agentTypes: agentTypes,
    existingVectorStoreId: ''
  });
  
  const [files, setFiles] = useState([]);
  const [processedFiles, setProcessedFiles] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentStep, setCurrentStep] = useState('setup'); // setup, processing, success, error
  const [processingProgress, setProcessingProgress] = useState(0);

  const [{ data, error, fetching }, createVectorStore] = useGlobalAction(api.createVectorStoreWithFiles);

  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const bgColor = useColorModeValue('gray.50', 'gray.700');

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      // Handle rejected files
      rejectedFiles.forEach(fileRejection => {
        console.warn('File rejected:', fileRejection.file.name, fileRejection.errors);
      });
    }

    const newFiles = acceptedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'pending' // pending, processing, completed, error
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true
  });

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({
          name: file.name,
          content: e.target.result,
          mimeType: file.type || 'application/octet-stream'
        });
      };
      reader.onerror = reject;
      
      if (file.type.startsWith('text/') || file.type === 'application/json') {
        reader.readAsText(file);
      } else {
        reader.readAsDataURL(file);
      }
    });
  };

  const processFiles = async () => {
    setProcessingProgress(0);
    const processed = [];
    
    for (let i = 0; i < files.length; i++) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === files[i].id ? { ...f, status: 'processing' } : f
        ));
        
        const fileContent = await readFileContent(files[i].file);
        processed.push(fileContent);
        
        setFiles(prev => prev.map(f => 
          f.id === files[i].id ? { ...f, status: 'completed' } : f
        ));
        
        setProcessingProgress(((i + 1) / files.length) * 50); // Files processing is 50% of total
      } catch (error) {
        setFiles(prev => prev.map(f => 
          f.id === files[i].id ? { ...f, status: 'error' } : f
        ));
        console.error('Error processing file:', files[i].name, error);
      }
    }
    
    setProcessedFiles(processed);
    return processed;
  };

  const handleSubmit = async () => {
    if (mode === 'existing') {
      if (!formData.existingVectorStoreId.trim()) {
        return;
      }
      
      setCurrentStep('processing');
      setProcessingProgress(100);
      
      // For existing vector store, just return the ID
      setCurrentStep('success');
      if (onSuccess) {
        onSuccess({ id: formData.existingVectorStoreId.trim() });
      }
      return;
    }

    if (!formData.name.trim()) {
      return;
    }

    setCurrentStep('processing');
    
    try {
      // Process files first
      const processedFileContent = await processFiles();
      setProcessingProgress(60);

      // Create vector store
      const result = await createVectorStore({
        name: formData.name,
        description: formData.description,
        textContent: formData.textContent,
        files: processedFileContent,
        chunkSize: formData.chunkSize,
        chunkOverlap: formData.chunkOverlap,
        institution: formData.institution,
        agentTypes: formData.agentTypes
      });

      setProcessingProgress(100);

      if (result?.success) {
        setCurrentStep('success');
        if (onSuccess) {
          onSuccess(result.result);
        }
      } else {
        setCurrentStep('error');
      }
    } catch (err) {
      console.error('Error creating vector store:', err);
      setCurrentStep('error');
    }
  };

  const handleClose = () => {
    if (!fetching) {
      setCurrentStep('setup');
      setMode('create');
      setFiles([]);
      setProcessedFiles([]);
      setProcessingProgress(0);
      setFormData({
        name: '',
        description: '',
        textContent: '',
        chunkSize: 1000,
        chunkOverlap: 200,
        institution: institution,
        agentTypes: agentTypes,
        existingVectorStoreId: ''
      });
      onClose();
    }
  };

  const handleRetry = () => {
    setCurrentStep('setup');
    setProcessingProgress(0);
    setFiles(prev => prev.map(f => ({ ...f, status: 'pending' })));
  };

  const canSubmit = mode === 'existing' 
    ? formData.existingVectorStoreId.trim() 
    : formData.name.trim() && (files.length > 0 || formData.textContent.trim());

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 'processing':
        return (
          <VStack spacing={4} py={8}>
            <Spinner size="xl" color="blue.500" />
            <Text fontSize="lg" fontWeight="semibold">Creating Vector Store...</Text>
            <Box w="100%">
              <Progress value={processingProgress} colorScheme="blue" size="lg" />
              <Text mt={2} fontSize="sm" color="gray.600" textAlign="center">
                {processingProgress < 50 ? 'Processing files...' : 
                 processingProgress < 100 ? 'Creating vector store...' : 'Finalizing...'}
              </Text>
            </Box>
            {files.length > 0 && (
              <VStack spacing={2} w="100%">
                {files.map(file => (
                  <HStack key={file.id} w="100%" justify="space-between" p={2} bg={bgColor} rounded="md">
                    <HStack>
                      <Icon as={FiFile} />
                      <Text fontSize="sm">{file.name}</Text>
                    </HStack>
                    {file.status === 'processing' && <Spinner size="sm" />}
                    {file.status === 'completed' && <Icon as={FiCheckCircle} color="green.500" />}
                    {file.status === 'error' && <Icon as={FiXCircle} color="red.500" />}
                  </HStack>
                ))}
              </VStack>
            )}
          </VStack>
        );

      case 'success':
        return (
          <VStack spacing={4} py={8}>
            <Icon as={FiCheckCircle} boxSize={16} color="green.500" />
            <Text fontSize="lg" fontWeight="semibold">Vector Store Created Successfully!</Text>
            {data?.result && (
              <VStack spacing={2}>
                <Text fontSize="sm" color="gray.600">Vector Store Details:</Text>
                <Box p={4} bg={bgColor} rounded="md" w="100%">
                  <Text fontSize="sm"><Text as="span" fontWeight="semibold">Name:</Text> {formData.name}</Text>
                  {formData.description && (
                    <Text fontSize="sm"><Text as="span" fontWeight="semibold">Description:</Text> {formData.description}</Text>
                  )}
                  <Text fontSize="sm"><Text as="span" fontWeight="semibold">Files:</Text> {files.length}</Text>
                  {formData.textContent && (
                    <Text fontSize="sm"><Text as="span" fontWeight="semibold">Has Text Content:</Text> Yes</Text>
                  )}
                </Box>
              </VStack>
            )}
          </VStack>
        );

      case 'error':
        return (
          <VStack spacing={4} py={8}>
            <Icon as={FiXCircle} boxSize={16} color="red.500" />
            <Text fontSize="lg" fontWeight="semibold">Error Creating Vector Store</Text>
            {error && (
              <Alert status="error">
                <AlertIcon />
                <AlertDescription>{error.toString()}</AlertDescription>
              </Alert>
            )}
            <Button colorScheme="blue" onClick={handleRetry}>
              Try Again
            </Button>
          </VStack>
        );

      default:
        return (
          <VStack spacing={6}>
            {/* Mode Toggle */}
            <Tabs w="100%" index={mode === 'create' ? 0 : 1} onChange={(index) => setMode(index === 0 ? 'create' : 'existing')}>
              <TabList>
                <Tab>Create New Vector Store</Tab>
                <Tab>Use Existing Vector Store</Tab>
              </TabList>
              
              <TabPanels>
                {/* Create New Tab */}
                <TabPanel px={0} py={4}>
                  <VStack spacing={6}>
                    {/* Basic Information */}
                    <VStack spacing={4} w="100%">
                      <FormControl isRequired>
                        <FormLabel>Vector Store Name</FormLabel>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Enter a name for your vector store"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Description</FormLabel>
                        <Textarea
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Optional description of what this vector store contains"
                          rows={2}
                        />
                      </FormControl>
                    </VStack>

                    <Divider />

                    {/* File Upload */}
                    <VStack spacing={4} w="100%">
                      <Text fontSize="md" fontWeight="semibold" alignSelf="flex-start">Upload Files</Text>
                      
                      <Box
                        {...getRootProps()}
                        w="100%"
                        minH="120px"
                        border="2px dashed"
                        borderColor={isDragActive ? 'blue.400' : borderColor}
                        borderRadius="lg"
                        p={6}
                        textAlign="center"
                        cursor="pointer"
                        bg={isDragActive ? 'blue.50' : bgColor}
                        transition="all 0.2s"
                        _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
                      >
                        <input {...getInputProps()} />
                        <VStack spacing={2}>
                          <Icon as={FiUploadCloud} boxSize={8} color="gray.500" />
                          <Text fontSize="sm" color="gray.600">
                            {isDragActive ? 'Drop files here...' : 'Drag & drop files here, or click to browse'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            Supports PDF, TXT, MD, DOC, DOCX, CSV, JSON (max 50MB each)
                          </Text>
                        </VStack>
                      </Box>

                      {files.length > 0 && (
                        <VStack spacing={2} w="100%" align="stretch">
                          <Text fontSize="sm" fontWeight="medium">Selected Files ({files.length})</Text>
                          <Box maxH="150px" overflowY="auto">
                            {files.map(file => (
                              <HStack key={file.id} p={2} bg={bgColor} rounded="md" justify="space-between">
                                <HStack>
                                  <Icon as={FiFile} />
                                  <VStack spacing={0} align="flex-start">
                                    <Text fontSize="sm">{file.name}</Text>
                                    <Text fontSize="xs" color="gray.500">{formatFileSize(file.size)}</Text>
                                  </VStack>
                                </HStack>
                                <Button size="xs" variant="ghost" onClick={() => removeFile(file.id)}>
                                  <Icon as={FiXCircle} />
                                </Button>
                              </HStack>
                            ))}
                          </Box>
                        </VStack>
                      )}
                    </VStack>

                    <Divider />

                    {/* Text Content */}
                    <VStack spacing={4} w="100%">
                      <FormControl>
                        <FormLabel>Direct Text Content</FormLabel>
                        <Textarea
                          value={formData.textContent}
                          onChange={(e) => setFormData(prev => ({ ...prev, textContent: e.target.value }))}
                          placeholder="Or paste text content directly here..."
                          rows={4}
                        />
                        <FormHelperText>
                          You can add text content directly instead of or in addition to uploading files.
                        </FormHelperText>
                      </FormControl>
                    </VStack>

                    {/* Advanced Settings */}
                    <Box w="100%">
                      <Button
                        variant="ghost"
                        leftIcon={<Icon as={FiSettings} />}
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        size="sm"
                      >
                        Advanced Settings
                      </Button>
                      
                      <Collapse in={showAdvanced} animateOpacity>
                        <VStack spacing={4} mt={4} p={4} bg={bgColor} rounded="md">
                          <HStack w="100%" spacing={4}>
                            <FormControl>
                              <FormLabel fontSize="sm">Chunk Size</FormLabel>
                              <NumberInput
                                value={formData.chunkSize}
                                onChange={(valueString) => setFormData(prev => ({ 
                                  ...prev, 
                                  chunkSize: parseInt(valueString) || 1000 
                                }))}
                                min={100}
                                max={4000}
                                size="sm"
                              >
                                <NumberInputField />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <FormHelperText fontSize="xs">
                                Size of text chunks (100-4000 characters)
                              </FormHelperText>
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="sm">Chunk Overlap</FormLabel>
                              <NumberInput
                                value={formData.chunkOverlap}
                                onChange={(valueString) => setFormData(prev => ({ 
                                  ...prev, 
                                  chunkOverlap: parseInt(valueString) || 200 
                                }))}
                                min={0}
                                max={formData.chunkSize / 2}
                                size="sm"
                              >
                                <NumberInputField />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <FormHelperText fontSize="xs">
                                Overlap between chunks (0-{Math.floor(formData.chunkSize / 2)})
                              </FormHelperText>
                            </FormControl>
                          </HStack>
                        </VStack>
                      </Collapse>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* Use Existing Tab */}
                <TabPanel px={0} py={4}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Vector Store ID</FormLabel>
                      <Input
                        value={formData.existingVectorStoreId}
                        onChange={(e) => setFormData(prev => ({ ...prev, existingVectorStoreId: e.target.value }))}
                        placeholder="Enter the ID of an existing vector store"
                      />
                      <FormHelperText>
                        Provide the ID of a vector store that was created manually or through another process.
                      </FormHelperText>
                    </FormControl>
                    
                    <Alert status="info" rounded="md">
                      <AlertIcon />
                      <VStack align="flex-start" spacing={0}>
                        <AlertTitle fontSize="sm">Using Existing Vector Store</AlertTitle>
                        <AlertDescription fontSize="sm">
                          This option allows you to reference a vector store that already exists in your OpenAI account. 
                          Make sure the ID is correct and the vector store contains the appropriate content for your use case.
                        </AlertDescription>
                      </VStack>
                    </Alert>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" closeOnOverlayClick={!fetching}>
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          {currentStep === 'setup' && 'Create Vector Store'}
          {currentStep === 'processing' && 'Creating Vector Store'}
          {currentStep === 'success' && 'Vector Store Created'}
          {currentStep === 'error' && 'Creation Failed'}
        </ModalHeader>
        {!fetching && <ModalCloseButton />}
        
        <ModalBody overflowY="auto">
          {getStepContent()}
        </ModalBody>

        <ModalFooter>
          {currentStep === 'setup' && (
            <HStack>
              <Button variant="ghost" onClick={handleClose} isDisabled={fetching}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isDisabled={!canSubmit || fetching}
                isLoading={fetching}
              >
                {mode === 'existing' ? 'Use Vector Store' : 'Create Vector Store'}
              </Button>
            </HStack>
          )}
          
          {currentStep === 'success' && (
            <Button colorScheme="green" onClick={handleClose}>
              Done
            </Button>
          )}
          
          {currentStep === 'error' && (
            <HStack>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleRetry}>
                Try Again
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default VectorStoreCreationModal;